import "./Card.css";
import {
  Stack,
  useTheme,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { useCallback, useRef, useState, useMemo, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { ThemeProvider } from "styled-components";
import { AgGridReact } from "ag-grid-react";
import { ConditionalWrapper } from "../utils/ConditionalWrapper";
import { useArrayData } from "../../context/ArrayDataContext";
import RejectPopUp from "./RejectPopUp";
import { RenderTable } from "../common/splitview-table/RenderTable";
import { mapPopupToApproverRow } from "./ApproverMapper";
import { useLocation } from "react-router-dom";
import { ApproverResponse } from "../../types/approverViewResponse";
import { getApproverView } from "../../api/approverViewApi";
import { ApproveWorkflowRequest } from "../../types/approveViewPostReqRes";
import { approveViewPostApi } from "../../api/approverViewPostApi";
import {
  formatTwoDecimals,
  formatOneDecimal,
  formatValueNoDecimals,
} from "../utils/formatTwoDecimals";
import { gridTheme } from "../../utils/tableUtils";
import { useSnackbar } from "../SnakBar/AppSnackbarProvider";
import ButtonComponent from "../common/button/Button";
import { APP_CONSTANTS } from "../../utils/constants";
import { useDispatch, useSelector } from "react-redux";
import {
  setSelectedDropdownValue,
  setSelectedRow,
} from "../../redux/actions/supportTicket";
import Download from "../common/DownloadGrid/Download";
import PageChangeWarningDialog from "../common/PageChangeWarningDialog";
import { setApiCall } from "../../redux/actions/userSlice";
import { hasRolePermission } from "../../constants/roleAccess";
import BulkPasteFilter from "../common/BulkFilter/BulkFilter";
import FilterManagerButton from "../common/FilterVariant/FilterManagerButton";

export const ApproverView = () => {
  const { upsertItem, items } = useArrayData();
  const { isSelectionRemove } = useSelector(
    (state: any) => state?.supportTicket || {},
  );
  const theme = useTheme();
  const location = useLocation();
  const { accounts } = useMsal();
  const dispatch = useDispatch();
   const [isTriggerFilter, setIsTriggerFilter] = useState(false);
  const queryParams = new URLSearchParams(location.search);
  const rawPlant = queryParams.get("plant"); // "5040 - Ballina" or "5040"
  const plantFromUrl = rawPlant?.split("-")[0]?.trim(); // "5040"
  const regionFromUrl = queryParams.get("region");
  const [rehydrated, setRehydrated] = useState<any[]>([]);
  const [approverData, setApproverData] = useState<ApproverResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [submitting, setSubmitting] = useState(false);
  const [minimized, setMinimized] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const gridRef = useRef<AgGridReact>(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [openRejectPopup, setOpenRejectPopup] = useState(false);
  const [selectedRow, setSelectedRows] = useState(null);
  const [openApproveToast, setOpenApproveToast] = useState(false);
  const [splitView, SetSplitView] = useState(false);
  const [isGridReady, setGridReady] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [openApproveConfirm, setOpenApproveConfirm] = useState(false);
  const [openPageChangeWarning, setOpenPageChangeWarning] = useState(false);
  const selectedRowIdsRef = useRef<Set<string>>(new Set());
  const selectionSnapshotRef = useRef<Set<string>>(new Set());
  const prevPageRef = useRef<number | null>(null);
  const pendingPageRef = useRef<number | null>(null);
  const suppressPaginationRef = useRef(false);
  const freezeSelectionRef = useRef(false);
  const lastInitFetchKeyRef = useRef<string | null>(null);
  const currentRole = useSelector(
    (state: any) => state?.userRoles?.currentRole || null,
  );
  const isRoleReady = Boolean(currentRole);
  const canApproveWorkflow =
    isRoleReady && hasRolePermission(currentRole, "approve_workflow");
  const selectedPlant = useSelector(
    (state: any) => state?.userRoles?.selectedPlant || [],
  );

  type ToastType = "success" | "error";
  const [toast, setToast] = useState<{
    open: boolean;
    msg: string;
    type: ToastType;
  }>({
    open: false,
    msg: "",
    type: "success",
  });

  // Fallback rehydrate if context is empty after a hard reload
  useEffect(() => {
    const raw = sessionStorage.getItem("approverRows");
    if (raw) {
      try {
        setRehydrated(JSON.parse(raw));
      } catch {
        /* ignore */
      }
    }
  }, []);
  const resolvePlantId = useCallback(() => {
    const plantFromRedux =
      Array.isArray(selectedPlant) && selectedPlant.length > 0
        ? String(selectedPlant[0])
        : "";

    const rawSessionPlant = sessionStorage.getItem("selectedPlant");
    let plantFromSession = "";
    if (rawSessionPlant) {
      try {
        const parsed = JSON.parse(rawSessionPlant);
        if (Array.isArray(parsed) && parsed.length > 0) {
          plantFromSession = String(parsed[0]);
        }
      } catch {
        plantFromSession = "";
      }
    }

    return (
      plantFromUrl ||
      plantFromRedux.split("-")[0]?.trim() ||
      plantFromSession.split("-")[0]?.trim() ||
      "5040"
    );
  }, [plantFromUrl, selectedPlant]);

  const fetchData = useCallback(async () => {
    if (!accounts || accounts.length === 0) return;
    setLoading(true);
    dispatch(setApiCall(true));
    const plantId = resolvePlantId();
    try {
      const primary = accounts[0];
      const approverId = primary?.idTokenClaims?.preferred_username;
      const data = await getApproverView(plantId, approverId);
      setApproverData(data);
    } catch (err) {
      setError("Failed to load approver data");
    } finally {
      setLoading(false);
       dispatch(setApiCall(false));
    }
  }, [accounts, dispatch, resolvePlantId]);

  useEffect(() => {
    if (!accounts || accounts.length === 0) return;

    const primary = accounts[0];
    const approverId = String(primary?.idTokenClaims?.preferred_username || "");
    const plantId = resolvePlantId();
    const fetchKey = `${approverId}__${plantId}`;

    if (lastInitFetchKeyRef.current === fetchKey) {
      return;
    }

    lastInitFetchKeyRef.current = fetchKey;
    fetchData();
  }, [accounts, fetchData, resolvePlantId]);

  useEffect(() => {
    const proposals = (location.state as any)?.proposals as any[] | undefined;
    if (Array.isArray(proposals) && proposals.length) {
      proposals.forEach((p) => upsertItem(p));
      try {
        const existing = sessionStorage.getItem("approverRows");
        const prior = existing ? JSON.parse(existing) : [];
        sessionStorage.setItem(
          "approverRows",
          JSON.stringify([...prior, ...proposals]),
        );
      } catch {}
    }
  }, [location.state, upsertItem]);

  const source = items?.length ? items : rehydrated;

  const toFieldName = (key: string) => {
    if (!key) return key as any;
    const parts = key.split(" ");
    parts[0] = parts[0].charAt(0).toLowerCase() + parts[0].slice(1);
    return parts.join("").replace(/[^\w]/g, "");
  };

  type ColDefLite = { field: string; headerName: string };
  type ColOrGroup =
    | string
    | {
        groupHeaderName: string;
        children: string[];
        marryChildren?: boolean;
      };

  const existingCols = useMemo(() => {
    const sample = (
      source?.length ? mapPopupToApproverRow(source[0]) : {}
    ) as Record<string, any>;
    return new Map(
      Object.keys(sample).map((key) => {
        const field = toFieldName(key);
        return [key, { field, headerName: key } as ColDefLite];
      }),
    );
  }, [source]);
  const handleClosePopup = () => {
    setOpenRejectPopup(false);
    setTimeout(() => {
      setSelectedRows(null);
    }, 2000);
  };

  const handleSavePopup = (approverData) => {
    setApproverData((prev) =>
      prev.filter(
        (item) => item.materialNumber !== approverData.materialNumber,
      ),
    );

    setOpenRejectPopup(false);
    setSelectedRows(null);
    setToastOpen(true);
  };

  const restoreSelectionSnapshot = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    const snapshot = selectionSnapshotRef.current;
    if (!snapshot.size) return;

    api.forEachNode((node: any) => {
      const nodeId = String(node.id);
      node.setSelected(snapshot.has(nodeId));
    });

    const rows = api.getSelectedRows?.() ?? [];
    setSelectedItems(rows);
    dispatch(setSelectedRow(rows));
  }, [dispatch]);

  const onSelectionChanged = useCallback(() => {
    if (freezeSelectionRef.current) return;
    const selectedNodes = gridRef.current?.api.getSelectedNodes() ?? [];
    selectedRowIdsRef.current = new Set(selectedNodes.map((n: any) => String(n.id)));
    const selectedRows = gridRef.current?.api.getSelectedRows() ?? [];
    setSelectedItems(selectedRows);
    dispatch(setSelectedRow(selectedRows));
    dispatch(
      setSelectedDropdownValue({
        menuId: "Approver",
        categoryId: "Data",
        functionId: "Data",
      }),
    );
  }, [dispatch]);

  const handlePageChange = useCallback((e?: any) => {
    if (suppressPaginationRef.current) return;
    const api = gridRef.current?.api;
    if (!api) return;

    const current = api.paginationGetCurrentPage?.() ?? 0;
    const prev = prevPageRef.current;

    if (prev === null) {
      prevPageRef.current = current;
      return;
    }

    if (current !== prev) {
      if (selectedRowIdsRef.current.size > 0) {
        freezeSelectionRef.current = true;
        selectionSnapshotRef.current = new Set(selectedRowIdsRef.current);
        suppressPaginationRef.current = true;
        api.paginationGoToPage(prev);
        suppressPaginationRef.current = false;
        pendingPageRef.current = current;
        setOpenPageChangeWarning(true);
      } else {
        api.deselectAll();
        selectedRowIdsRef.current = new Set();
        setSelectedItems([]);
        prevPageRef.current = current;
      }
    }
  }, []);

  const handlePageChangeConfirm = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;

    freezeSelectionRef.current = false;
    selectionSnapshotRef.current = new Set();
    api.deselectAll();
    selectedRowIdsRef.current = new Set();
    setSelectedItems([]);
    dispatch(setSelectedRow([]));

    if (pendingPageRef.current !== null) {
      suppressPaginationRef.current = true;
      api.paginationGoToPage(pendingPageRef.current);
      suppressPaginationRef.current = false;
      prevPageRef.current = pendingPageRef.current;
      pendingPageRef.current = null;
    }

    setOpenPageChangeWarning(false);
  }, [dispatch]);

  const handlePageChangeCancel = useCallback(() => {
    pendingPageRef.current = null;
    freezeSelectionRef.current = false;
    restoreSelectionSnapshot();
    setOpenPageChangeWarning(false);
  }, [restoreSelectionSnapshot]);

  const DeSelectItems = () => {
    const api = gridRef.current?.api;
    if (api) api.deselectAll();
    selectedRowIdsRef.current = new Set();
    setSelectedItems([]);
  };
  useEffect(() => {
    if (openPageChangeWarning) return;
    if (!isSelectionRemove) return;
    DeSelectItems();
  }, [isSelectionRemove, openPageChangeWarning]);
  const columns = useMemo(() => {
    // Map existing data keys -> column defs so we reuse fields where possible
    const existingCols = new Map(
      Object.keys(approverData[0] ?? {}).map((key) => {
        const field = toFieldName(key);
        return [key, { field, headerName: key } as ColDefLite];
      }),
    );

    const desiredOrder: ColOrGroup[] = [
      "Extraction Date",
      "IO Run Type",
      "Approval status",
      "Approver",
      "MRP controller",
      "MRP controller desc",
      "BCP Flag",
      // "End User",
      "Material Plant",
      "Planning Lead",
      "Material Number",
      // Your text had "Material Description." with a trailing period; removed for cleaner UI. Tell me if you want the period kept.
      "Material Description",
      "Approval Required",
      "Review Flag for IO Writeback",
      "End User action",
      "Reason",
      "Comments",

      // �� Group: End User proposal ��
      {
        groupHeaderName: "End User proposal",
        marryChildren: true,
        children: [
          "Total consolidated Safety Stock (weeks)-EU",
          "Safety Time (weeks)-EU",
          "Coverage Profile (weeks)-EU",
          "Safety Stock (qty)-EU",
        ],
      },

      // �� Group: Consolidated Safety Stock (weeks) ��
      {
        groupHeaderName: "Consolidated Safety Stock (weeks)",
        marryChildren: true,
        children: ["IO Consolidated SS(weeks)", "S4 Consolidated SS(weeks)"],
      },

      // �� Group: Comparisons ��
      {
        groupHeaderName: "Comparisons",
        marryChildren: true,
        children: ["EU proposal vs IO %", "EU proposal vs S4 %"],
      },

      "Approver actions",
      //   "Consolidated Safety Stock (wk) - Approved",
      //   "VAR (wk) - Approved SS vs S4",
      //   "VAR (%) - Approved SS vs S4",
      //   "VAR (USD) - Approved SS vs S4",
      //   "VAR (PDR) - Approved SS vs S4",
      //   "Item Type",
      //   "Weekly Demand (Qty)",
      //   "End User accepts IO Proposal?",
      //   "Consolidated Safety Stock (USD) - Approved",
      //   "Consolidated Safety Stock (USD) - S4",
      //   "Part Units pear EA (PDR/EA)", // keeping your exact text; say the word and I�ll change "pear" -> "per"
    ];

    // Helper: build a single leaf column from a header (create if missing)
    const dateHeaders = new Set(["Extraction Date"]);
    const dateSortComparator = (a: any, b: any) => {
      const toTime = (v: any) => {
        if (!v || v === "-") return 0;
        const t = new Date(v).getTime();
        return isNaN(t) ? 0 : t;
      };
      return toTime(a) - toTime(b);
    };
    const ensureCol = (header: string): ColDefLite => {
      const existing = existingCols.get(header);
      const col: any = existing ? { ...existing } : { field: toFieldName(header), headerName: header };
      if (dateHeaders.has(header)) col.comparator = dateSortComparator;
      return col;
    };

    // Build final columnDefs, supporting groups
    const columnDefs: any[] = [];
    desiredOrder.forEach((entry) => {
      if (typeof entry === "string") {
        columnDefs.push(ensureCol(entry));
      } else {
        const { groupHeaderName, children, marryChildren = true } = entry;
        const childDefs = children.map((h) => ensureCol(h));
        columnDefs.push({
          headerName: groupHeaderName,
          marryChildren,
          children: childDefs,
        });
      }
    });
    const takeActionRenderer = (params: any) => {
      if (!canApproveWorkflow) {
        return null;
      }

      return (
        <ButtonComponent
          className="take-action"
          type="secondary"
          label="Take Action"
          borderRadius="28px"
          onClick={() => {
            // open your RejectPopUp with the clicked row
            setSelectedRows(params.data);
            setOpenRejectPopup(true);
          }}
        />
      );
    };

    for (let i = 0; i < columnDefs.length; i++) {
      const col = columnDefs[i];
      // if grouped, dive into children
      if (col?.children?.length) {
        col.children = col.children.map((child: any) =>
          child?.headerName === "Approver actions" ||
          child?.field === toFieldName("Approver actions")
            ? {
                ...child,
                field: toFieldName("Approver actions"),
                headerName: "Approver actions",
                sortable: false,

                filter: false,
                cellRenderer: takeActionRenderer,
                // keep column narrow and centered
                width: 200,
                hide: !canApproveWorkflow,
                cellClass: "ag-center-cols-container",
              }
            : child,
        );
      } else if (
        col?.headerName === "Approver actions" ||
        col?.field === toFieldName("Approver actions")
      ) {
        columnDefs[i] = {
          ...col,
          field: toFieldName("Approver actions"),
          headerName: "Approver actions",
          sortable: false,
          filter: false,
          cellRenderer: takeActionRenderer,
          width: 200,
          hide: !canApproveWorkflow,
          cellClass: "ag-center-cols-container",
        };
      }
    }

    return columnDefs;
  }, [approverData, canApproveWorkflow]);

  const parseNumberOrText = (value: any) => {
    if (value === null || value === undefined) return "";
    const v = String(value).trim();
    if (v === "-" || v === "") return "";
    const num = Number(v);
    return Number.isFinite(num) ? num : v; // allow text if needed
  };

  const rows = useMemo(() => {
    const byId = new Map<string, any>();
    const dataToUse = approverData;

    dataToUse.forEach((s) => {
      const id = String(s?.workflowId ?? "").trim(); // Assuming `materialNumber` is the unique identifier
      const key = id || `${s?.workflowId ?? ""}`;
      if (!byId.has(key)) byId.set(key, s);
    });

    const unique = Array.from(byId.values());
    const mapped = unique.map((r, idx) => {
      const obj: any = { ...r };
      obj.__uid = r.id ?? `${r.materialNumber ?? "mat"}__${idx}`;
      obj.__raw = { ...r }; // Preserve the original unmapped/raw data
      // Ensure each field corresponds to the correct value based on your column mapping
      columns
        .flatMap((c: any) => (c.children ? c.children : [c]))
        .forEach((leaf: any) => {
          const header = leaf.headerName || leaf.field;

          // Mapping column headers to actual data fields approverName
          // EU proposal vs IO % formula
          //�EU SS proposal vs IO % - the differences between End User proposed SS vs IO recommendation in percentage

          const userConsolidatedSafetyStock = Number(
            parseNumberOrText(r?.["userConsolidatedSafetyStock"]),
          );
          const ioConsolidatedSafetyStock = Number(
            parseNumberOrText(r?.["ioConsolidatedSafetyStockRounded"]),
          );

          let euSsProposalVsIoPercent = 0;

          // Avoid Infinity / NaN
          if (
            ioConsolidatedSafetyStock &&
            Number.isFinite(userConsolidatedSafetyStock)
          ) {
            euSsProposalVsIoPercent =
              ((userConsolidatedSafetyStock - ioConsolidatedSafetyStock) /
                ioConsolidatedSafetyStock) *
              100;
          }

          // Update only if changed
          const formattedIoPercent = !ioConsolidatedSafetyStock
            ? "N/A"
            : Math.round(euSsProposalVsIoPercent) + "%";
          if (r["euSsProposalVsIoPercent"] !== formattedIoPercent) {
            r["euSsProposalVsIoPercent"] = formattedIoPercent;
          }

          // EU proposal vs S4 % formula
          // EU SS proposal vs S4 % - the differences between End User proposed SS vs S4 SS in percentage

          const s4ConsolidatedSafetyStock = Number(
            parseNumberOrText(r?.["s4ConsolidatedSafetyStock"]),
          );

          let euSsProposalVsS4Percent = 0;

          // Avoid Infinity / NaN
          if (
            s4ConsolidatedSafetyStock &&
            Number.isFinite(userConsolidatedSafetyStock)
          ) {
            euSsProposalVsS4Percent =
              ((userConsolidatedSafetyStock - s4ConsolidatedSafetyStock) /
                s4ConsolidatedSafetyStock) *
              100;
          }

          r["euSsProposalVsS4Percent"] = !s4ConsolidatedSafetyStock
            ? "N/A"
            : Math.round(euSsProposalVsS4Percent) + "%";

          switch (header) {
            case "Extraction Date":
              obj[header] = r.ioExtractionDate ?? "";
              break;
            case "IO Run Type":
              obj[header] = r.ioRunTypeLabel || "";
              break;
            case "BCP Flag":
              obj[header] = r.bpcFlag || "";
              break;
            case "Approval Required":
              obj[header] = r.approval_required || "";
              break;
            case "Review Flag for IO Writeback":
              obj[header] = r.reviewRequired ?? "";
              break;
            case "Planning Lead":
              obj[header] = r.planningLead ?? "";
              break;
            case "Comments":
              obj[header] = r.userComments || ""; // Map userComments to Comments
              break;
            case "Approver":
              obj[header] = r.approverName || ""; // Map userComments to Comments
              break;
            case "MRP controller":
              obj[header] =
                r.mrpControllerId !== null && r.mrpControllerId !== undefined
                  ? String(r.mrpControllerId) // convert number to string
                  : "-"; // Map mrpController to MRP controller
              break;
            case "MRP controller desc":
              obj[header] = r.mrpControllerDescription || ""; // Map mrpControllerDesc to MRP controller desc
              break;
            case "Reason":
              obj[header] = r.userReasonCode || ""; // Map userReason to Reason
              break;
            case "Safety Stock (qty)-EU":
              obj[header] = formatValueNoDecimals(r.userSafetyStockQty || 0); // Map userSafetyStockQty to Safety Stock (EU)
              break;
            case "Coverage Profile (weeks)-EU":
              obj[header] = formatOneDecimal(r.userCoverageProfile || 0);
              obj[header] = formatOneDecimal(r.userCoverageProfile || 0);
              break;
            case "Safety Time (weeks)-EU":
              obj[header] = formatOneDecimal(r.userSafetyTime || 0);
              obj[header] = formatOneDecimal(r.userSafetyTime || 0);
              break;
            case "Total consolidated Safety Stock (weeks)-EU":
              obj[header] = formatOneDecimal(
                r.userConsolidatedSafetyStock || 0,
              );
              break;
            case "IO Consolidated SS(weeks)":
              obj[header] = formatOneDecimal(
                r.ioConsolidatedSafetyStockRounded || 0,
              );
              break;
            case "S4 Consolidated SS(weeks)":
              obj[header] = formatOneDecimal(r.s4ConsolidatedSafetyStock || 0);
              obj[header] = formatOneDecimal(r.s4ConsolidatedSafetyStock || 0);
              break;
            case "Selected Choice":
              obj[header] = r.userSelectedChoice || ""; // Map userSelectedChoice to Selected Choice
              break;
            case "VAR (Pct) Approved SS vs S4":
              obj[header] = formatTwoDecimals(r.varPctApprovedSSvsS4 || ""); // Map varPctApprovedSSvsS4 to VAR (Pct) Approved SS vs S4
              obj[header] = formatTwoDecimals(r.varPctApprovedSSvsS4 || ""); // Map varPctApprovedSSvsS4 to VAR (Pct) Approved SS vs S4
              break;
            case "End User action":
              obj[header] = r.userSelectedChoice || ""; // Map userSelectedChoice to End User action
              break;
            case "EU proposal vs IO %":
              obj[header] =
                r.euSsProposalVsIoPercent === "N/A"
                  ? "N/A"
                  : formatTwoDecimals(r.euSsProposalVsIoPercent || "0%");
              break;
            case "EU proposal vs S4 %":
              obj[header] =
                r.euSsProposalVsS4Percent === "N/A"
                  ? "N/A"
                  : formatTwoDecimals(r.euSsProposalVsS4Percent || "0%");
              break;
            default:
              obj[header] = r[header] || ""; // Default mapping if header doesn't match
          }

          // Mirror to field key so ag-Grid finds it via `field`
          const field = toFieldName(header);
          if (!obj.hasOwnProperty(field)) obj[field] = obj[header];
        });
      return obj;
    });

    return mapped;
  }, [approverData, columns]); // React to changes in approverData and columns

  const sideBar = {
    toolPanels: [
      {
        id: "columns",
        labelDefault: "Columns",
        labelKey: "columns",
        iconKey: "columns",
        toolPanel: "agColumnsToolPanel",
        toolPanelParams: {
          suppressRowGroups: true,
          suppressValues: true,
          suppressPivots: true,
          suppressPivotMode: true,
        },
      },
    ],
    defaultToolPanel: "",
  };

  const handleEvent = (action) => {
    setMinimized((prev) => (prev === action ? "" : action));
  };

  const handleGridChange = (data) => {
    // console.log("grid change ", data);
    // console.log("items context ", items);
  };

  const buildBulkPayload = (): ApproveWorkflowRequest[] => {
    return selectedItems.map((row) => ({
      workflowId: row.workflowId,
      approverId: row.approverId,

      approverSelectedChoice: "End User Proposal",
      approverComments: row?.approverComments || "",
      approverReasonCode: APP_CONSTANTS.EndUserSSApproved,

      approvedSafetyStockQty: Number(row.userSafetyStockQty ?? 0),
      approvedCoverageProfile: Number(row.userCoverageProfile ?? 0),
      approvedSafetyTime: Number(row.userSafetyTime ?? 0),

      approvedConsolidatedSafetyStock: Number(
        row.approvedConsolidatedSafetyStock ?? 0,
      ),

      varWkApprovedSSvsS4: Number(row.varWkApprovedSSvsS4 ?? 0),
      varPctApprovedSSvsS4: String(row.varPctApprovedSSvsS4 ?? "0"),

      consolidatedSafetyStockUsdApproved: Number(
        row.consolidatedSafetyStockUsdApproved ?? 0,
      ),
      varUsdApprovedSSvsS4: String(row.varUsdApprovedSSvsS4 ?? "0%"),
      varPdrApprovedSSvsS4: Number(row.varPdrApprovedSSvsS4 ?? 0),
    }));
  };

  const handleBulkApprove = async () => {
    if (!canApproveWorkflow) return;
    if (selectedItems.length < 1) return;
    const inProgressId = enqueueSnackbar({
      message: "Submitting materials for Approval",
      severity: "warning",
      persist: true,
    });

    try {
      setOpenApproveConfirm(false);
      setSubmitting(true);
      const payload = buildBulkPayload();
      await approveViewPostApi(payload);
      enqueueSnackbar({
        message: "Approval Captured Successfully",
        severity: "success",
        autoHideDuration: 2500,
      });
      fetchData();
    } catch (error) {
      console.error("Bulk approve failed", error);
      enqueueSnackbar({
        message: `Failed to Submit the Material`,
        severity: "error",
        autoHideDuration: 2000,
      });
    } finally {
      setSubmitting(false);
      closeSnackbar(inProgressId);
    }
  };

  const getApproverId = () => {
    const primary = accounts[0];
    const approverId = primary?.idTokenClaims?.preferred_username;
    return approverId;
  };

  const handleResetFilters = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    api.setFilterModel(null);
    if (typeof api.setGridOption === "function") {
      api.setGridOption("quickFilterText", "");
    }
    setIsTriggerFilter(true);
    api.onFilterChanged();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <Stack gap={2} padding={2} sx={{ padding: 2, paddingLeft: 0, flex: 1 }}>
        <Stack
          direction="row"
          gap={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Box sx={{ display: "flex", flexDirection: "column", marginLeft: 1 }}>
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: 600,
              }}
            >
              Safety Stock Approval List
            </Typography>
          </Box>
          {/* Right-side controls */}
          <Stack direction="row" spacing={1} alignItems="center">
            <ConditionalWrapper condition={!splitView}>
              <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                gap={2}
                sx={{ width: "100%", paddingRight: 2 }}
              >
                {canApproveWorkflow && (
                  <ButtonComponent
                    borderRadius="28px"
                    className="take-action"
                    type="secondary"
                    label="Approve Safety Stock"
                    onClick={() => setOpenApproveConfirm(true)}
                    disabled={selectedItems.length < 1}
                  />
                )}
                {(approverData?.length ?? 0) > 0 && (
                  <><ButtonComponent
                    borderRadius="28px"
                    className="take-action"
                    type="secondary"
                    label="Reset Filters"
                    onClick={handleResetFilters}
                    width="auto"
                    maxWidth="none"
                  /><FilterManagerButton    
                             gridRef={gridRef}    
                             tableName="Safety Stock Approval List"    
                             userEmail={localStorage.getItem("userEmail") || ""}    
                             disabled={!approverData?.length}  />
                             <BulkPasteFilter
                  gridRef={gridRef}
                  columns={columns}
                  triggerClearFilter = {isTriggerFilter}
                  setIsTriggerFilter={setIsTriggerFilter}
                /> </>
                )}
                <Download
                  loading={loading}
                  gridRef={gridRef}
                  fileName="Safety Stock Approval List"
                  rowData={approverData}
                />
                {/* <Fab
                variant="extended"
                size="small"
                disabled={selectedItems.length < 2}
                sx={{
                  backgroundColor: selectedItems.length < 2 ? "#ccc" : "#000",
                  color: "#fff",
                  fontSize: "0.75rem",
                  minWidth: "180px",
                  height: "32px",
                  "&:hover": {
                    backgroundColor:
                      selectedItems.length < 2 ? "#ccc" : "#333",
                  },
                }}
                onClick={() => setOpenApproveConfirm(true)}
              >
                Approve Safety Stock
              </Fab> */}
              </Stack>
            </ConditionalWrapper>
          </Stack>
        </Stack>

        <Dialog
          open={openApproveConfirm}
          onClose={() => setOpenApproveConfirm(false)}
        >
          <DialogTitle>Confirm Approval</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to approve safety stock for{" "}
              <b>{selectedItems.length}</b> materials?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenApproveConfirm(false)} color="error">
              Cancel
            </Button>
            <Button
              // variant="contained"
              // color="success"
              onClick={handleBulkApprove}
            >
              Approve
            </Button>
          </DialogActions>
        </Dialog>
        {selectedRow && (
          <>
            {" "}
            <RejectPopUp
              open={openRejectPopup}
              onClose={handleClosePopup}
              onSave={handleSavePopup}
              data={selectedRow}
              approverId={getApproverId()}
              setToastOpen={setOpenApproveToast}
              fetchData={fetchData}
            />
          </>
        )}

        <RenderTable
          loading={loading}
          gridRef={gridRef}
          columns={columns}
          gridTheme={gridTheme}
          rows={rows}
          rowsPerPage={rowsPerPage}
          setGridReady={setGridReady}
          sideBar={sideBar}
          minimized={null}
          emitEvent={handleEvent}
          onChange={handleGridChange}
          setSelectedRow={setSelectedRows}
          setOpenRejectPopup={setOpenRejectPopup}
          onSelectionChanged={onSelectionChanged}
          withSelectionColumn={true}
          selectionScope="page"
          handleChangePage={handlePageChange}
          selectedCount={selectedItems.length}
        />
        <PageChangeWarningDialog
          open={openPageChangeWarning}
          selectedCount={selectedItems.length}
          onCancel={handlePageChangeCancel}
          onConfirm={handlePageChangeConfirm}
        />
        <Box paddingBottom={1}></Box>
      </Stack>
    </ThemeProvider>
  );
};
